import { useEffect, useState } from "react";
import AddBottomButton from "../../../components/Add/AddBottomButton";
import AddModel from "../../../components/Add/AddModel";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import DynamicField from "../../../components/DynamicComponent/DynamicField";
import InputField from "../../../components/inputfields/InputField";
import validateMandatoryFields from "../../../components/commonfunctions/validateMandatoryFields";
import useApi from "../../../services/useApi";
import InputLabel from "../../../components/inputfields/InputLabel";
import CustomRadioButton from "../../../components/buttons/CustomRadioButton";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";

export interface AddbankProps {
    edit?: boolean;
}
interface bankDetails {
    'bank_name': string;
    'bank_account_number': string;
    'account_type': string;
    'other_account_type': string;
    'charge_value': string;
}
interface error { [key: string]: string }

type InputKey = "Bank Name" | "Bank Account Number" | "Account Type" | "Other Account Type";

interface InputDetails {
    required: boolean;
    disabled: boolean;
    id: string;
    type: string;
    tabIndex: number;
    condition: boolean;
    className?: string;
    options?: string[];
    maxLength?: number;
}

export function Addbank({ edit = false, }: AddbankProps) {
    //states and hooks use here
    const initialState = {
        'bank_name': '',
        'bank_account_number': '',
        'account_type': '',
        'other_account_type': '',
        'charge_value': ''
    }
    const { PostAPI, getAPI, PatchAPI } = useApi()
    const [loading, setLoading] = useState(false)
    const [editLoading, setEditLoading] = useState(false)
    const [errors, setErrors] = useState<error>(initialState)
    const [state, setState] = useState<bankDetails>(initialState)
    const [temporaryData, setTemporaryData] = useState<bankDetails>(initialState)
    const [mode, setMode] = useState('Percentage')
    const [tempMode, setTempMode] = useState('Percentage')
    const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams()
    //object used for ui mapping
    const inputs: Record<InputKey, InputDetails> = {
        "Bank Name": {
            required: true,
            disabled: false || edit,
            id: "bank_name",
            type: "input",
            tabIndex: 0,
            condition: true,
            className: 'w-full',
            maxLength: 60,
        },
        "Bank Account Number": {
            required: true,
            disabled: false || edit,
            id: "bank_account_number",
            type: "input",
            tabIndex: 1,
            condition: true,
            className: 'w-full',
            maxLength: 60,
        },
        "Account Type": {
            required: true,
            disabled: false || edit,
            id: "account_type",
            type: "dropdown",
            tabIndex: 2,
            condition: true,
            className: 'w-full',
            options: ['Savings Account', 'Current Account', 'Fixed Deposit Account', 'Islamic Account', 'Other']
        },
        "Other Account Type": {
            required: true,
            disabled: false || edit,
            id: "other_account_type",
            type: "input",
            tabIndex: 3,
            condition: state?.account_type === "Other",
            className: 'w-full',
            maxLength: 60,
        },
    };

    //function to handle input 
    const handleInput = (id: string, value: string) => {
        // disabling inspec and edit of the field 
        if (edit && id !== 'charge_value') {
            return;
        }
        setErrors((prev) => ({
            ...prev,
            [id]: ''
        }))
        if (id === "account_type") {
            setState((prev) => ({
                ...prev,
                ['other_account_type']: ''
            }))
        }
        if (id === 'charge_value' && value !== '') {

            if (mode === 'Percentage') {
                const regex = /^(?:100(?:\.0{1,2})?|(?:[0-9]?\d)(?:\.\d{0,2})?)$/
                if (!regex.test(value)) {
                    return
                }

            } else {
                const regex = /^\d+(\.\d{0,2})?$/
                if (!regex.test(value)) {
                    return;
                }
            }

        }
        setState((prev) => ({
            ...prev,
            [id]: value
        }))
    }

    //function to handle radio button
    const handleRadioButton = (value: string) => {
        setState((prev) => ({
            ...prev,
            'charge_value': ''
        }))
        setMode(value)
    }

    //function to add bank
    const handleBankAdd = async () => {
        //setting states to initial value
        setErrors(initialState)

        //empty field check
        let requiredFields = ['bank_name', 'bank_account_number', 'account_type'];
        if (state.account_type === "Other") {
            requiredFields.push('other_account_type')
        }

        const errorObject = validateMandatoryFields(state, requiredFields);
        if (Object.keys(errorObject).length > 0) {
            setErrors(errorObject);
            return;
        }
        //checking  whether the value entered in the charge_value field is 0
        // if (Number(state.charge_value) <= 0) {
        //     setErrors((prev) => ({
        //         ...prev,
        //         'charge_value': `Bank charge ${mode === 'Percentage' ? 'percentage' : 'amount'} must exceed 0`
        //     }))
        //     return;
        // }

        //no errors,then create payload for the api
        let payload = {}
        if (edit) {
            payload = {
                "charge_mode": state?.charge_value !== '' ? mode.toUpperCase() : '',
                "charge_value": state?.charge_value !== '' ? state?.charge_value : ''
            }
        } else {
            payload = {
                "name": state.bank_name.trim(),
                "account_number": state.bank_account_number.trim(),
                "account_type": state.account_type,
                ...(state.account_type === "Other" && { "custom_account_type": state.other_account_type }),
                ...(state.charge_value !== '' && { "charge_mode": mode.toUpperCase() }),
                ...(state.charge_value !== '' && { "charge_value": Number(state?.charge_value) }),
            }
        }

        //once payload is ready ,hit the api
        setLoading(true);
        let response;
        if (edit) {
            response = await PatchAPI(`banks/${id}`, payload);
        } else {
            response = await PostAPI(`banks/`, payload)
        }
        if (!response?.error) {
            dispatch(
                showToast({
                    type: "success",
                    message: edit ? 'Bank details updated successfully' : response?.data?.message,
                })
            );
            navigate(pathname ? pathname : '/other-resources/banks');
        } else {
            console.log(response, 'API ERROR')
            dispatch(
                showToast({
                    type: "error",
                    message: response?.data?.response?.data?.message,
                })
            );
        }
        setLoading(false)
    }

    //function to fetch bank details using the api
    const fetchBankDetails = async () => {
        setEditLoading(true)
        let response = await getAPI(`banks/${id}`);
        if (!response.error) {
            let obj = {
                'bank_name': response?.data?.data?.name,
                'bank_account_number': response?.data?.data?.account_number,
                'account_type': response?.data?.data?.account_type,
                'other_account_type': response?.data?.data?.custom_account_type,
                'charge_value': response?.data?.data?.charge_value || '',
            }
            setState(obj);
            setMode(response?.data?.data?.charge_mode === "PERCENTAGE" || !response?.data?.data?.charge_mode ? 'Percentage' : 'AFA');
            setTemporaryData(obj);
            setTempMode(response?.data?.data?.charge_mode === "PERCENTAGE" || !response?.data?.data?.charge_mode ? 'Percentage' : 'AFA');
        } else {
            console.log(response, 'API FAILED')
        }
        setEditLoading(false)
    }

    //update button disabling function
    const handleDisabler = () => {
        if (edit && mode === tempMode && state?.charge_value === temporaryData?.charge_value) {
            return true;
        }
        return false;
    }
    //useEffect to fetch the data for editing bank
    useEffect(() => {
        if (edit) {
            fetchBankDetails();
        }
    }, [])
    return (
      <div>
        <Breadcrumbs
          heading={`${edit ? "Edit" : "Add"} Bank`}
          navPath={[
            "Other Resources",
            "Banks",
            "List of Banks",
            `${edit ? "Edit" : "Add"} Bank`,
          ]}
          links={
            pathname && edit
              ? [pathname, pathname, pathname]
              : ["/other-resources/banks", "/other-resources/banks", "/other-resources/banks"]
          }
        />
        <form
          onSubmit={(e) => {
            loading ? "" : e.preventDefault();
            handleBankAdd();
          }}
        >
          <AddModel noScroll={false}>
            {/* first row of details with other account type  */}
            <div className="-mx-6 flex flex-wrap">
              {Object.keys(inputs).map(
                (item: string, index) =>
                  inputs[item as InputKey].condition && (
                    <div className="w-full sm:w-1/2 lg:w-1/3 px-6 flex relative mb-6" key={index}>
                      <DynamicField
                        fieldData={inputs[item as InputKey]}
                        label={item}
                        errors={errors}
                        onInputChange={(id, value) => handleInput(id, value)}
                        inputValues={state}
                        handleDropdownSelect={handleInput}
                        editLoading={editLoading}
                      />
                    </div>
                  )
              )}
            </div>

            {/* mode of charges field  */}
            <div className="-mx-6 flex flex-col sm:flex-row flex-wrap">
              <div className="w-full sm:w-1/2 lg:w-1/3 px-6 mb-6 sm:mb-0">
                <InputLabel labelName="Mode of Charges" id="mode_of_charge" />
                <div className="-ml-3">
                  <CustomRadioButton
                    options={[
                      { id: "Percentage", label: "Percentage" },
                      { id: "AFA", label: "AFA" },
                    ]}
                    selected={mode}
                    onChange={(value) => handleRadioButton(value)}
                  />
                </div>
              </div>
              <div className="w-full sm:w-1/2 lg:w-1/3 px-6">
                <InputField
                  inputItem={{
                    labelName: `${
                      mode === "AFA"
                        ? "Bank Charge Amount"
                        : "Bank Charge Percentage"
                    }`,
                    id: "charge_value",
                  }}
                  errorMessage={errors ? errors["charge_value"] : ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleInput(event.target.id, event.target.value)
                  }
                  value={state?.charge_value}
                  tabIndex={5}
                  className={"w-full"}
                  maindivstyle="w-full"
                  editLoading={editLoading}
                />
              </div>
            </div>
          </AddModel>
          <AddBottomButton
            buttonName={edit ? "Update Bank" : "Add Bank"}
            onClick={handleBankAdd}
            loading={loading}
            buttonClass={`whitespace-nowrap`}
            buttonWidth={`${edit ? "w-[185px]" : "w-[130px]"}`}
            testID="add_bank_btn"
            disabled={handleDisabler()}
          />
        </form>
      </div>
    );
}
